// src/Components/Promo/UploadMailingList.js

import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  CircularProgress,
  Typography,
  TextField,
  Autocomplete,
  InputAdornment,
  TableSortLabel,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useUploadMailingListManage } from '../../Hooks/useUploadMailingListManage';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import {
  MenuAddNew,
  MenuSave,
  MenuSaveGrey,
  MenuDelete,
  MenuIconCancel,
  MenuDeleteGrey,
  MenuDownload,
  MenuAddNewGrey,
} from '../../assets/icons/CustomIcons';

import { MenuUpload } from '../../assets/icons/CustomIconsAdd2';

// --- CONSTANTS ---
const ORANGE_COLOR = '#FF9900';
const DARK_BLUE = '#00008B';
const LIGHT_BLUE_BG = '#E3F2FD';

// --- CUSTOM TRIANGLE ICON ---
const CustomSortIcon = ({ active, className }) => {
  return (
    <svg className={className} focusable='false' viewBox='0 0 24 24' aria-hidden='true'>
      {active ? (
        <path d='M12 8l6 8H6z' fill='currentColor' />
      ) : (
        <path
          d='M12 8l6 8H6z'
          fill='none'
          stroke='currentColor'
          strokeWidth='1'
          strokeLinejoin='round'
        />
      )}
    </svg>
  );
};

// --- AMENDED EDITABLE CELL ---
const EditableCell = ({
  value,
  onChange,
  autoFocus,
  placeholder,
  isEditing,
  onExitEdit,
}) => {
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      e.target.blur();
    }
  };

  const handleBlur = (e) => {
    const container = e.currentTarget.closest('tr');
    if (container && container.contains(e.relatedTarget)) {
      return;
    }
    if (onExitEdit) onExitEdit();
  };

  if (isEditing) {
    return (
      <TextField
        value={value}
        autoFocus={autoFocus}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        variant='standard'
        size='small'
        fullWidth
        InputProps={{
          disableUnderline: true,
          style: { fontSize: '0.8rem', color: DARK_BLUE, fontWeight: 'normal' },
        }}
        sx={{
          backgroundColor: LIGHT_BLUE_BG,
          px: 1,
          borderRadius: 1,
          border: `1px solid ${ORANGE_COLOR}`,
          boxSizing: 'border-box',
          '& .MuiInputBase-root': {
            height: '100%',
            fontSize: '0.8rem',
            color: DARK_BLUE,
          },
          '& .MuiInputBase-input': {
            color: DARK_BLUE,
            fontWeight: 'normal',
          },
          '&:hover': { backgroundColor: LIGHT_BLUE_BG },
          '&:focus-within': { backgroundColor: LIGHT_BLUE_BG },
        }}
      />
    );
  }

  // Left justify the green tick for Ok status
  if (placeholder === 'Status' && value === 'Ok') {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          minHeight: '20px',
          px: 1,
        }}
        title='Ok'>
        <CheckCircleIcon sx={{ color: 'green', fontSize: '1.2rem' }} />
      </Box>
    );
  }

  // Left justify the text and add red cross for non-Ok statuses
  if (placeholder === 'Status') {
    if (!value || value.trim() === '') {
      return <Box sx={{ minHeight: '20px', px: 1 }} />;
    }
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          minHeight: '20px',
          px: 1,
          gap: 0.5,
        }}
        title={value}>
        <CancelIcon sx={{ color: '#d32f2f', fontSize: '1.2rem', flexShrink: 0 }} />
        <Typography
          variant='body2'
          sx={{
            color: 'black',
            fontSize: '0.8rem',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textAlign: 'left',
          }}>
          {value}
        </Typography>
      </Box>
    );
  }

  return (
    <Typography
      variant='body2'
      title={value}
      sx={{
        color: 'black',
        minHeight: '20px',
        px: 1,
        fontSize: '0.8rem',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}>
      {value}
    </Typography>
  );
};

export default function UploadMailingList({ setIsDirty }) {
  const { state, flags, alerts, actions } = useUploadMailingListManage();

  const fileInputRef = useRef(null);
  const [focusedField, setFocusedField] = useState(null);

  // Local importing state to trigger the visual spinner in the dashboard
  const [isImporting, setIsImporting] = useState(false);

  useEffect(() => {
    if (!state.focusedRowId) {
      setFocusedField(null);
    }
  }, [state.focusedRowId]);

  // --- SEARCH STATES ---
  const [searchVal, setSearchVal] = useState(null);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [activeSearchOptions, setActiveSearchOptions] = useState([]);

  // Base options generated from rows
  const baseSearchOptions = React.useMemo(() => {
    if (!state.displayRows) return [];
    return state.displayRows.map((row) => ({
      label: `${row.userName || 'No Name'} | ${row.companyName || 'No Company'} | ${row.email || 'No Email'}`,
      id: row.id,
    }));
  }, [state.displayRows]);

  useEffect(() => {
    setSearchVal(null);
    setSearchInputValue('');
    setActiveSearchOptions([]);
    if (actions.setFocusedRowId) {
      actions.setFocusedRowId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Dirty State & Browser Unload Protection
  useEffect(() => {
    if (setIsDirty) setIsDirty(flags.isDirty);

    const handleBeforeUnload = (e) => {
      if (flags.isDirty) {
        e.preventDefault();
        e.returnValue = 'Cancel without Save will discard your changes?';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [flags.isDirty, setIsDirty]);

  const ICON_SIZE = 64;
  const toolBlockStyle = {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    minWidth: 0,
  };

  const formatDate = (dateVal) => {
    if (!dateVal) return '';
    if (typeof dateVal.toDate === 'function') {
      return dateVal.toDate().toLocaleDateString();
    }
    return new Date(dateVal).toLocaleDateString();
  };

  const handleDownload = () => {
    if (!state.displayRows || state.displayRows.length === 0) return;

    // Added Status back into export
    const headerRow = ['Date', 'User Name', 'Company', 'Email', 'Status'];
    const dataRows = state.displayRows.map((row) => [
      formatDate(row.addedAt),
      row.userName,
      row.companyName || '',
      row.email,
      row.status || '',
    ]);
    const worksheet = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);

    const wscols = [{ wch: 15 }, { wch: 20 }, { wch: 20 }, { wch: 30 }, { wch: 20 }];
    worksheet['!cols'] = wscols;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Uploaded Mailing List');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    });

    saveAs(data, `UploadedMailingList_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const disableDownload = !state.displayRows || state.displayRows.length === 0;

  // --- UPLOAD HANDLERS ---
  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Turn on the dashboard spinner overlay
    setIsImporting(true);

    // SetTimeout forces the browser to render the spinner before blocking the thread with XLSX processing
    setTimeout(() => {
      const reader = new FileReader();
      reader.onload = async (evt) => {
        // CRITICAL FIX: XLSX.read is highly CPU intensive and locks the main Javascript thread.
        // The MUI CircularProgress animation starts as a tiny dot and expands. If the thread locks instantly,
        // it freezes as a dot. We pause here for 400ms to let the stroke animation expand to a full circle
        // before we lock the CPU.
        await new Promise((resolve) => setTimeout(resolve, 400));

        try {
          const data = new Uint8Array(evt.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          if (json.length === 0) {
            alerts.setResultSeverity('warning');
            alerts.setResultMessage('The selected file is empty.');
            alerts.setShowResultAlert(true);
            return;
          }

          const headers = json[0] || [];
          const h1 = headers[0] ? String(headers[0]).trim() : '';
          const h2 = headers[1] ? String(headers[1]).trim() : '';
          const h3 = headers[2] ? String(headers[2]).trim() : '';

          if (h1 !== 'User Name' || h2 !== 'Company' || h3 !== 'Email') {
            alerts.setResultSeverity('warning');
            alerts.setResultMessage(
              'Mailing list must have User Name, Company, Email on the first row',
            );
            alerts.setShowResultAlert(true);
            return;
          }

          const rowsToImport = json
            .slice(1)
            .map((r) => ({
              userName: r[0] || '',
              companyName: r[1] || '',
              email: r[2] || '',
            }))
            .filter((r) => r.email && String(r.email).trim() !== '');

          if (rowsToImport.length === 0) {
            alerts.setResultSeverity('warning');
            alerts.setResultMessage('No valid data rows found in the spreadsheet.');
            alerts.setShowResultAlert(true);
            return;
          }

          await actions.processImport(rowsToImport);
        } catch (error) {
          console.error('File parsing error:', error);
          alerts.setResultSeverity('error');
          alerts.setResultMessage(
            'Failed to parse the file. Ensure it is a valid Excel or CSV.',
          );
          alerts.setShowResultAlert(true);
        } finally {
          // Turn off the spinner and clear the input
          setIsImporting(false);
          if (fileInputRef.current) fileInputRef.current.value = '';
        }
      };
      reader.readAsArrayBuffer(file);
    }, 50);
  };

  // --- SEARCH HANDLERS ---
  const handleSearchSelect = (event, newValue) => {
    setSearchVal(newValue);
    if (newValue && newValue.id) {
      if (actions.setFocusedRowId) {
        actions.setFocusedRowId(newValue.id);
      }
      setTimeout(() => {
        const el = document.getElementById(`row-${newValue.id}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 50);
    } else {
      setSearchVal(null);
      setSearchInputValue('');
      setActiveSearchOptions([]);
      if (actions.setFocusedRowId) {
        actions.setFocusedRowId(null);
      }
    }
  };

  const handleInputChange = (event, newInputValue, reason) => {
    if (reason === 'clear') {
      setSearchVal(null);
      setSearchInputValue('');
      setActiveSearchOptions([]);
      if (actions.setFocusedRowId) actions.setFocusedRowId(null);
    } else {
      setSearchInputValue(newInputValue);
      if (newInputValue.trim().length > 0) {
        setActiveSearchOptions(baseSearchOptions);
      } else {
        setActiveSearchOptions([]);
      }
    }
  };

  // --- REUSABLE COLUMN HEADER STYLING ---
  const headerSx = {
    backgroundColor: '#fff3e0',
    fontWeight: 'bold',
    borderBottom: '2px solid #ff9800',
    py: '4px',
    textAlign: 'center',
  };

  const sortLabelSx = {
    '&.MuiTableSortLabel-root': {
      color: 'inherit',
    },
    '&.MuiTableSortLabel-root:hover': {
      color: 'inherit',
      '& .MuiTableSortLabel-icon': {
        opacity: 0.6,
      },
    },
    '& .MuiTableSortLabel-icon': {
      opacity: 0.4,
      width: '1.5em',
      height: '1.5em',
      color: '#000000',
      transition: 'opacity 0.2s, color 0.2s',
    },
    '&.Mui-active': {
      color: 'inherit',
    },
    '&.Mui-active .MuiTableSortLabel-icon': {
      opacity: 1,
      color: '#000000',
    },
  };

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <input
        type='file'
        accept='.xlsx, .xls, .csv'
        onChange={handleFileChange}
        style={{ display: 'none' }}
        ref={fileInputRef}
      />

      {/* TOOLBAR */}
      <Box
        sx={{
          height: '60px',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#f4f4f4',
          borderBottom: '1px solid #ccc',
          flexShrink: 0,
          zIndex: 50,
          px: '5px',
        }}>
        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={actions.handleAddNew}
            disabled={isImporting || !flags.canAddNew}>
            {isImporting || !flags.canAddNew ? (
              <MenuAddNewGrey sx={{ fontSize: ICON_SIZE }} />
            ) : (
              <MenuAddNew sx={{ fontSize: ICON_SIZE }} />
            )}
          </IconButton>
        </Box>

        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={handleUploadClick}
            disabled={isImporting}>
            <MenuUpload
              sx={{
                fontSize: ICON_SIZE,
              }}
              iconScale={5}
              iconX={32}
              iconY={38}
              textY={108}
            />
          </IconButton>
        </Box>

        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={handleDownload}
            disabled={disableDownload || isImporting}>
            <MenuDownload
              sx={{
                fontSize: ICON_SIZE,
                opacity: disableDownload ? 0.35 : 1,
                filter: disableDownload ? 'grayscale(100%)' : 'none',
              }}
              iconScale={5}
              iconX={32}
              iconY={38}
              textY={108}
            />
          </IconButton>
        </Box>

        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={actions.handleSave}
            disabled={!flags.canSave || isImporting}>
            {flags.canSave ? (
              <MenuSave sx={{ fontSize: ICON_SIZE }} />
            ) : (
              <MenuSaveGrey sx={{ fontSize: ICON_SIZE }} />
            )}
          </IconButton>
        </Box>

        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={actions.handleDeleteRequest}
            disabled={!flags.canDelete || isImporting}>
            {flags.canDelete ? (
              <MenuDelete sx={{ fontSize: ICON_SIZE }} />
            ) : (
              <MenuDeleteGrey sx={{ fontSize: ICON_SIZE }} />
            )}
          </IconButton>
        </Box>

        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            disabled={isImporting}
            onClick={() =>
              flags.isDirty ? alerts.setShowUnsaved(true) : actions.resetInterface()
            }>
            <MenuIconCancel sx={{ fontSize: ICON_SIZE }} />
          </IconButton>
        </Box>
      </Box>

      {/* SEARCH BAR */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#fdfdfd',
          borderBottom: '1px solid #ccc',
          flexShrink: 0,
          py: 1,
          width: '100%',
        }}>
        <Box
          sx={{
            width: '60%',
            flexShrink: 0,
            px: 1,
            boxSizing: 'border-box',
          }}>
          <Autocomplete
            value={searchVal}
            inputValue={searchInputValue}
            onChange={handleSearchSelect}
            onInputChange={handleInputChange}
            options={activeSearchOptions}
            getOptionLabel={(option) => option.label}
            clearOnBlur={false}
            componentsProps={{
              popper: {
                style: { zIndex: 100005 },
              },
            }}
            renderOption={(props, option) => (
              <li {...props}>
                <Typography sx={{ color: '#1a0dab', fontSize: '0.85rem' }}>
                  {option.label}
                </Typography>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder='Search by User Name, Company, or Email...'
                variant='outlined'
                size='small'
                inputProps={{
                  ...params.inputProps,
                  autoComplete: 'new-password',
                  form: {
                    autoComplete: 'off',
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position='start'>
                        <SearchIcon sx={{ color: DARK_BLUE, ml: 1 }} />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: '#fff',
                  },
                }}
              />
            )}
          />
        </Box>
      </Box>

      {/* CONTENT */}
      <Box
        sx={{
          flexGrow: 1,
          p: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}>
        {/* Local import overlay to keep the table visible but show progress */}
        {isImporting && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.6)',
              zIndex: 10,
            }}>
            <CircularProgress />
          </Box>
        )}

        {state.loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              height: '100%',
              width: '100%',
              boxSizing: 'border-box',
              borderTop: '2px solid #ff9800',
              borderBottom: 'none',
              borderRadius: 0,
              overflowY: 'auto',
            }}>
            <Table stickyHeader size='small' sx={{ width: '100%', tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow sx={{ height: '36px' }}>
                  <TableCell
                    sx={{ width: '40px', textAlign: 'center', p: 0, ...headerSx }}>
                    <Checkbox
                      color='primary'
                      indeterminate={
                        state.selectedIds.length > 0 &&
                        state.selectedIds.length < state.displayRows.length
                      }
                      checked={
                        state.displayRows.length > 0 &&
                        state.selectedIds.length === state.displayRows.length
                      }
                      onChange={(e) => actions.handleSelectAll(e.target.checked)}
                      size='small'
                      sx={{ p: 0.5 }}
                    />
                  </TableCell>

                  {/* DATE COLUMN */}
                  <TableCell
                    sx={{
                      width: '10%',
                      resize: 'horizontal',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      ...headerSx,
                    }}>
                    <TableSortLabel
                      active={state.sortConfig?.key === 'addedAt'}
                      direction={
                        state.sortConfig?.key === 'addedAt'
                          ? state.sortConfig.direction
                          : 'asc'
                      }
                      onClick={() =>
                        actions.requestSort && actions.requestSort('addedAt')
                      }
                      IconComponent={(props) => (
                        <CustomSortIcon
                          {...props}
                          active={state.sortConfig?.key === 'addedAt'}
                        />
                      )}
                      sx={sortLabelSx}>
                      Date
                    </TableSortLabel>
                  </TableCell>

                  {/* USER NAME COLUMN */}
                  <TableCell
                    sx={{
                      width: '20%',
                      resize: 'horizontal',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      ...headerSx,
                    }}>
                    <TableSortLabel
                      active={state.sortConfig?.key === 'userName'}
                      direction={
                        state.sortConfig?.key === 'userName'
                          ? state.sortConfig.direction
                          : 'asc'
                      }
                      onClick={() =>
                        actions.requestSort && actions.requestSort('userName')
                      }
                      IconComponent={(props) => (
                        <CustomSortIcon
                          {...props}
                          active={state.sortConfig?.key === 'userName'}
                        />
                      )}
                      sx={sortLabelSx}>
                      User Name
                    </TableSortLabel>
                  </TableCell>

                  {/* COMPANY COLUMN */}
                  <TableCell
                    sx={{
                      width: '20%',
                      resize: 'horizontal',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      ...headerSx,
                    }}>
                    <TableSortLabel
                      active={state.sortConfig?.key === 'companyName'}
                      direction={
                        state.sortConfig?.key === 'companyName'
                          ? state.sortConfig.direction
                          : 'asc'
                      }
                      onClick={() =>
                        actions.requestSort && actions.requestSort('companyName')
                      }
                      IconComponent={(props) => (
                        <CustomSortIcon
                          {...props}
                          active={state.sortConfig?.key === 'companyName'}
                        />
                      )}
                      sx={sortLabelSx}>
                      Company
                    </TableSortLabel>
                  </TableCell>

                  {/* EMAIL COLUMN */}
                  <TableCell
                    sx={{
                      width: '25%',
                      resize: 'horizontal',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      ...headerSx,
                    }}>
                    <TableSortLabel
                      active={state.sortConfig?.key === 'email'}
                      direction={
                        state.sortConfig?.key === 'email'
                          ? state.sortConfig.direction
                          : 'asc'
                      }
                      onClick={() => actions.requestSort && actions.requestSort('email')}
                      IconComponent={(props) => (
                        <CustomSortIcon
                          {...props}
                          active={state.sortConfig?.key === 'email'}
                        />
                      )}
                      sx={sortLabelSx}>
                      Email
                    </TableSortLabel>
                  </TableCell>

                  {/* STATUS COLUMN HEADER */}
                  <TableCell
                    sx={{
                      width: '20%',
                      resize: 'horizontal',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      ...headerSx,
                    }}>
                    <TableSortLabel
                      active={state.sortConfig?.key === 'status'}
                      direction={
                        state.sortConfig?.key === 'status'
                          ? state.sortConfig.direction
                          : 'asc'
                      }
                      onClick={() => actions.requestSort && actions.requestSort('status')}
                      IconComponent={(props) => (
                        <CustomSortIcon
                          {...props}
                          active={state.sortConfig?.key === 'status'}
                        />
                      )}
                      sx={sortLabelSx}>
                      Status
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {state.displayRows.map((row) => {
                  const isSelected = state.selectedIds.includes(row.id);
                  const isNewRow = row.id && row.id.startsWith('temp_');

                  const isFocusedRow = state.focusedRowId === row.id;

                  return (
                    <TableRow
                      id={`row-${row.id}`}
                      key={row.id}
                      hover
                      sx={{
                        height: '32px',
                        backgroundColor: isFocusedRow
                          ? 'rgba(0, 176, 255, 0.15) !important'
                          : 'inherit',
                        transition: 'background-color 0.5s ease',
                        '& td': { py: '4px' },
                        '&.Mui-selected': { backgroundColor: 'transparent !important' },
                        '&.Mui-selected:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
                        },
                      }}>
                      <TableCell
                        sx={{ textAlign: 'center', p: 0, cursor: 'pointer' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          actions.handleCheckboxChange(row.id);
                        }}>
                        <Checkbox
                          checked={isSelected}
                          size='small'
                          sx={{ pointerEvents: 'none', p: 0.5 }}
                        />
                      </TableCell>

                      <TableCell sx={{ maxWidth: 0, textAlign: 'center' }}>
                        <Typography
                          variant='body2'
                          title={isNewRow ? 'New' : formatDate(row.addedAt)}
                          sx={{
                            color: 'black',
                            fontSize: '0.8rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            textAlign: 'center',
                          }}>
                          {isNewRow ? 'New' : formatDate(row.addedAt)}
                        </Typography>
                      </TableCell>

                      <TableCell
                        sx={{ px: 1, maxWidth: 0, cursor: 'text' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          actions.setFocusedRowId(row.id);
                          setFocusedField('userName');
                        }}>
                        <EditableCell
                          value={row.userName || ''}
                          placeholder='Name'
                          isEditing={isNewRow || isFocusedRow}
                          autoFocus={
                            focusedField === 'userName' || (isNewRow && !focusedField)
                          }
                          onExitEdit={() => {
                            actions.setFocusedRowId(null);
                            setFocusedField(null);
                          }}
                          onChange={(val) =>
                            actions.handleFieldChange(row.id, 'userName', val)
                          }
                        />
                      </TableCell>

                      <TableCell
                        sx={{ px: 1, maxWidth: 0, cursor: 'text' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          actions.setFocusedRowId(row.id);
                          setFocusedField('companyName');
                        }}>
                        <EditableCell
                          value={row.companyName || ''}
                          placeholder='Company'
                          isEditing={isNewRow || isFocusedRow}
                          autoFocus={focusedField === 'companyName'}
                          onExitEdit={() => {
                            actions.setFocusedRowId(null);
                            setFocusedField(null);
                          }}
                          onChange={(val) =>
                            actions.handleFieldChange(row.id, 'companyName', val)
                          }
                        />
                      </TableCell>

                      <TableCell
                        sx={{ px: 1, maxWidth: 0, cursor: 'text' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          actions.setFocusedRowId(row.id);
                          setFocusedField('email');
                        }}>
                        <EditableCell
                          value={row.email || ''}
                          placeholder='Email'
                          isEditing={isNewRow || isFocusedRow}
                          autoFocus={focusedField === 'email'}
                          onExitEdit={() => {
                            actions.setFocusedRowId(null);
                            setFocusedField(null);
                          }}
                          onChange={(val) =>
                            actions.handleFieldChange(row.id, 'email', val)
                          }
                        />
                      </TableCell>

                      {/* STATUS DATA CELL */}
                      <TableCell
                        sx={{ px: 1, maxWidth: 0, cursor: 'default' }} // Default cursor because this shouldn't be edited manually
                        onClick={(e) => {
                          e.stopPropagation();
                          actions.setFocusedRowId(row.id);
                          // We don't setFocusedField('status') anymore because it's not editable.
                        }}>
                        <EditableCell
                          value={row.status || ''}
                          placeholder='Status'
                          isEditing={false} // Force read-only to show tick
                          autoFocus={false}
                          onExitEdit={() => {}}
                          onChange={() => {}}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* --- DIALOGS --- */}
      <ConfirmDialog
        open={alerts.showOverwriteAlert}
        onClose={() => alerts.setShowOverwriteAlert(false)}
        onConfirm={actions.executeSave}
        message='This email exists. Do you want to overwrite it?'
        severity='warning'
        confirmText='Yes'
        cancelText='Cancel'
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
      />

      <ConfirmDialog
        open={alerts.showUnsaved}
        onClose={() => alerts.setShowUnsaved(false)}
        onConfirm={() => {
          alerts.setShowUnsaved(false);
          actions.resetInterface();
        }}
        message='Cancel without Save will discard your changes?'
        severity='warning'
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
      />

      <ConfirmDialog
        open={alerts.showDelete}
        onClose={() => alerts.setShowDelete(false)}
        onConfirm={actions.handleConfirmDelete}
        message='Remove selected users from the import list?'
        severity='error'
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
      />

      <ConfirmDialog
        open={alerts.showResultAlert}
        onClose={() => alerts.setShowResultAlert(false)}
        message={alerts.resultMessage}
        severity={alerts.resultSeverity}
        autoClose={alerts.resultSeverity === 'success' ? 3000 : 0}
        onConfirm={() => alerts.setShowResultAlert(false)}
        cancelText={false}
        hideCancel={true}
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
      />
    </Box>
  );
}
